import db from "../db/database";
import { fetchFromBusinessCentral } from "../utils/businessCentralUtils";
import { esIdTpvReconciliable } from "../utils/idTpv";
import { anotarAviso } from "./syncLog";
import { esSelloPorCampo } from "./selloDeDocumentoBorrado";
import { esNumeroDeDocumentoUsable } from "../utils/cabeceraDeBC";

// Nombre con el que esta entidad aparece en las líneas de consola.
const ENTIDAD = "recuperación de facturas";

/**
 * ¿El error dice que Business Central no publica lo que se le preguntó?
 *
 * Un 404 sobre una entidad de OData no es «no hay datos» --eso sería una lista
 * vacía-- sino que el servicio web no está registrado con ese nombre. Y un 400
 * de «property not found» es lo mismo para un campo. Los dos significan que la
 * pregunta no se pudo hacer, no que la respuesta sea que no.
 *
 * Ya no decide el control de flujo --cualquier fallo sale por la misma puerta--
 * sino **el texto del aviso**, que es donde la distinción sigue sirviendo: esto
 * lo arregla alguien registrando un servicio web, y un timeout se arregla solo.
 */
const esEntidadNoPublicada = (error) => {
  const estado = error?.response?.status;
  if (estado === 404) return true;

  const mensaje = String(error?.response?.data?.error?.message || "");

  return estado === 400 && /could not find a property|does not exist/i.test(mensaje);
};

/**
 * Dónde buscar una factura según su tipo, en el orden en que se mira: primero
 * el borrador, después el documento registrado.
 *
 * Hasta que Business Central publicó `Id_TPV` en las entidades registradas
 * (bloque 1 del acuerdo 11), el guardia sólo miraba `salesInvoice`. Eso es el
 * hallazgo 5 de la auditoría: **el guardia dejaba de ver la factura en cuanto
 * se contabilizaba**, así que un reintento posterior la creaba de nuevo.
 */
const ENTIDADES_POR_TIPO = {
  "Credit Memo": { borrador: "SalesCreditMemo", registrada: "PostedSalesCreditMemo" },
  Invoice: { borrador: "salesInvoice", registrada: "PostedSalesInvoice" },
};

const entidadesDe = (documentType) =>
  ENTIDADES_POR_TIPO[documentType] || ENTIDADES_POR_TIPO.Invoice;

/**
 * Busca una factura en BC utilizando el Id_TPV.
 *
 * Devuelve el documento con `registrada` indicando en cuál de las dos apareció:
 * no es lo mismo encontrarla en borrador --hay que seguir enviándola-- que
 * encontrarla registrada, donde ya no queda nada que mandar.
 */
export const findInvoiceInBCByIdTpv = async (idTpv, documentType = "Invoice") => {
  try {
    // El vacío no busca, y el prefijo `SIN-` tampoco: ése lo inventa Business
    // Central cuando la factura le llega sin identificador, así que no
    // identifica nada. Buscar por él emparejaría dos ventas que sólo comparten
    // el no haberlo traído. Valor reservado del acuerdo 11.
    if (!esIdTpvReconciliable(idTpv)) return null;

    const { borrador, registrada } = entidadesDe(documentType);

    for (const [entidad, estaRegistrada] of [
      [borrador, false],
      [registrada, true],
    ]) {
      const response = await fetchFromBusinessCentral(`${entidad}?$filter=Id_TPV eq '${idTpv}'`);

      // El sello de un documento borrado lleva el mismo `Id_TPV` que la venta
      // --Business Central lo copia con `TransferFields`--, así que esta
      // consulta puede devolver los dos. Se descarta acá y no en el `$filter`
      // por un motivo concreto: el campo sólo existe en las entidades
      // registradas y desde la 1.0.4.96. Pedirlo en el filtro daría 400 en la
      // pasada del borrador y en cualquier ambiente sin esa versión, y un
      // guardia que devuelve error equivale a decir «esta factura no está» --
      // que es justo lo que hace que el reintento la cree por segunda vez.
      //
      // Descartar acá no puede fallar: donde el campo no viaja, `undefined` no
      // es `true` y todo pasa como hoy.
      const documento = (response ?? []).find((d) => !esSelloPorCampo(d));

      if (documento) {
        return { ...documento, registrada: estaRegistrada };
      }
    }

    return null;
  } catch (error) {
    // **Sólo un 200 con su lista delante permite decir «esta factura no está».**
    // Cualquier otra cosa significa «no pude preguntar», y devolver null ahí
    // equivale a contestar que no: es justo lo que hace que el reintento cree
    // la factura por segunda vez.
    //
    // Hasta ahora sólo se relanzaban las dos formas de «Business Central no
    // publica lo que preguntas». El resto --un timeout, un 500, un throttling,
    // la red caída-- se tragaba con un `console.error`, apoyándose en que «no
    // tiene sentido detener la venta por un fallo de red». Y no la detiene:
    // **ninguna de las seis llamadas a este guardia está en el camino de la
    // venta**, las seis son de sincronización. Así que fallar cerrado no impide
    // cobrar, sólo aplaza un envío al ciclo siguiente --que es exactamente lo
    // que se quiere cuando no se sabe si el documento ya está--.
    //
    // Ninguna de las dos puertas es hipotética. El 404: el 27/08/2026 un
    // despliegue dejó `PostedSalesInvoice` y `PostedSalesCreditMemo` sin
    // registrar durante tres cuartos de hora. Y un 400 que no es de campo
    // inexistente: una empresa recién creada, sin series de numeración de venta
    // configuradas, contesta `Application_CallbackNotAllowed` en vez de lista
    // vacía, porque la página intenta abrir el asistente de series y por OData
    // no hay a quién enseñárselo. Medido en Panamá producción el 20/09/2026, y
    // le pasará a cada país nuevo su primer día.
    anotarAviso(
      ENTIDAD,
      `no se pudo comprobar si la factura ${idTpv} ya está en Business Central: ${error.message}. ` +
        (esEntidadNoPublicada(error)
          ? "Business Central no publica lo que se le pregunta: hay que registrar el servicio web. "
          : "") +
        "No se envía hasta poder comprobarlo, para no crearla dos veces."
    );

    throw error;
  }
};

/**
 * Libera una factura que quedó bloqueada sincronizando
 */
export const releaseStuckInvoice = async (invoiceId) => {
  try {
    await db.invoices.update(invoiceId, {
      syncing: false,
      syncStartTime: null,
    });
  } catch (error) {
    console.error(`Error liberando factura ${invoiceId}`, error);
  }
};

/**
 * Reconciliar factura local con BC
 */
export const reconcileInvoiceWithBC = async (localInvoice, bcInvoice) => {
  try {
    // Si apareció ya registrada no queda nada que enviar: reenviarle líneas o
    // pagos sería escribir sobre un documento fiscal emitido. Se cierra local.
    if (bcInvoice.registrada) {
      await db.invoices.update(localInvoice.id, {
        No: bcInvoice.No || null,
        systemId: bcInvoice.SystemId || null,
        syncStage: "posted",
        pendingSync: 0,
        syncing: false,
        syncStartTime: null,
        lastSynced: new Date(),
      });

      console.warn(`Factura ya registrada en BC: ${bcInvoice.No}`);

      return true;
    }

    // Un borrador sin número usable no se adopta.
    //
    // Business Central llegó a dejar borradores con `No.` vacío --la cabecera de
    // un abono cuya caja no tenía serie, medido en Costa Rica TEST el
    // 12/09/2026--. Adoptarlo dejaba el documento con `Pre_Assigned_No` nulo y
    // pidiendo sus líneas y sus pagos con `Document_No eq ''`. Se sigue sin él:
    // el envío vuelve a pedir la cabecera, y Business Central limpia ese
    // borrador o dice por qué no puede numerar. Acuerdo
    // `contrato-hallazgos-ciclo-cri-2026-09-12.md`, H4.
    if (!esNumeroDeDocumentoUsable(bcInvoice.No)) {
      anotarAviso(
        ENTIDAD,
        `${localInvoice.Id_TPV || localInvoice.id} tiene en Business Central un borrador sin número: no se adopta y se vuelve a pedir la cabecera`
      );
      return false;
    }

    let invoiceToUpdate = {
      Pre_Assigned_No: bcInvoice.No || null,
      systemId: bcInvoice.SystemId || null,

      syncStage: "header",
      pendingSync: 1,
      syncing: false,
      syncStartTime: null,
      lastSynced: new Date(),
    };

    let lines = await fetchFromBusinessCentral(
      `LinesSalesInvoice?$filter=Document_No eq '${bcInvoice.No}'`
    );

    if (lines.length > 0) {
      invoiceToUpdate.syncStage = "lines";
    }

    let paymentLines = await fetchFromBusinessCentral(
      `DetallePagoFactura?$filter=Document_No_ eq '${bcInvoice.No}'`
    );

    if (paymentLines.length > 0) {
      invoiceToUpdate.syncStage = "payments";
      invoiceToUpdate.pendingSync = 0;
    }

    await db.invoices.update(localInvoice.id, invoiceToUpdate);

    console.warn(`Factura reconciliada desde BC: ${bcInvoice.No}`);

    return true;
  } catch (error) {
    console.error(`Error reconciliando factura ${localInvoice.id}`, error);

    return false;
  }
};

/**
 * Recovery principal
 *
 * Busca facturas:
 * - pendientes
 * - con Id_TPV
 * - sin No asignado
 *
 * y verifica si ya existen en BC.
 */
export const recoverInterruptedInvoices = async () => {
  try {
    console.warn("Iniciando recovery de facturas interrumpidas...");

    const interruptedInvoices = await db.invoices
      .filter(
        (invoice) =>
          (invoice.pendingSync === 1 || invoice.pendingSync === true) &&
          (invoice.syncing === 1 || invoice.syncing === true) &&
          invoice.Id_TPV &&
          !invoice.deleted
      )
      .toArray();

    if (!interruptedInvoices.length) {
      console.warn("No hay facturas pendientes para recovery");

      return;
    }

    console.warn(`Facturas encontradas para recovery: ${interruptedInvoices.length}`);

    for (const invoice of interruptedInvoices) {
      try {
        console.warn(`Verificando factura ${invoice.id} - ${invoice.Id_TPV}`);

        // Buscar en BC
        const bcInvoice = await findInvoiceInBCByIdTpv(invoice.Id_TPV, invoice.Document_Type);

        // Existe en BC
        if (bcInvoice) {
          await reconcileInvoiceWithBC(invoice, bcInvoice);

          continue;
        }

        // NO existe en BC
        // liberar lock local
        await releaseStuckInvoice(invoice.id);

        console.warn(`Factura ${invoice.id} no existe en BC. Lock liberado.`);
      } catch (error) {
        console.error(`Error procesando recovery de factura ${invoice.id}`, error);
      }
    }

    console.warn("Recovery de facturas finalizado");
  } catch (error) {
    console.error("Error general en recoverInterruptedInvoices", error);
  }
};
